import React from "react";
import { Link } from "gatsby";
import { Container, Row, Col } from "react-bootstrap";

import PageWrapper from "../../components/PageWrapper";
import BlogPostSEO from "../../components/SEO/BlogPostSEO";
import { Section, Title, Text, Box, Badge } from "../../components/Core";

import styled from "styled-components";

import titleImage from "../../assets/image/jpeg/old-mailbox.jpg";
import iconQuote from "../../assets/image/png/quote-icon.png";
import styles from "../../assets/styles/blog.module.css";

// Prices were read from auspost.com.au on 4 Nov 2026 (retail prices effective
// 1 July 2026). Re-check them there before editing; do not update from memory.

const Post = styled(Box)`
  overflow: hidden;
  font-size: 1rem;

  h2, h3, h4, h5, h6, p, blockquote, pre, ul, ol {
    margin-bottom: 1.25rem;
    &:last-child { margin-bottom: 0; }
  }
  h2, h3, h4, h5, h6 {
    margin-top: 2.25rem;
    margin-bottom: 1.25rem;
    color: ${({ theme }) => theme.colors.heading} !important;
  }
  ol li { list-style-type: decimal; }
  ul li { list-style-type: disc; }
  blockquote {
    margin-bottom: 1.25rem;
    padding-left: 50px;
    position: relative;
    color: ${({ theme }) => theme.colors.text} !important;
    font-size: 20px;
    &::after {
      content: url(${iconQuote});
      display: inline-block;
      min-width: 28px;
      max-width: 28px;
      margin-top: 8px;
      margin-right: 23px;
      position: absolute;
      top: 0;
      left: 0;
    }
  }
  table {
    width: 100%;
    margin-bottom: 1.5rem;
    border-collapse: collapse;
    th, td {
      padding: 10px 12px;
      border: 1px solid #e6e6ec;
      vertical-align: top;
    }
    th {
      background: #f7f7fb;
      font-weight: 700;
      text-align: left;
    }
  }
  .table-scroll {
    overflow-x: auto;
  }
  img, iframe, video {
    max-width: 100%;
    margin-bottom: 2rem;
    display: block;
  }
`;

const BadgePost = ({ children }) => (
  <Badge mr={3} mb={3} bg="#eae9f2" color="#696871" fontSize="16px" px={3} py={2}>
    {children}
  </Badge>
);

const PageDetails = () => {
  return (
    <>
      <BlogPostSEO
        title="Australia Post mail redirection: 2026 costs and how it works"
        slug="guides/australia-post-mail-redirection"
        description="What Australia Post mail redirection costs in 2026, concession and pensioner rates, what it won't redirect, and when a mail service suits you better."
        datePublished="2026-11-04"
        image={titleImage}
      />
      <PageWrapper footerDark>
        <Section className="pb-0">
          <div className="pt-5"></div>
          <Container>
            <Row className="justify-content-center text-center">
              <Col lg="12">
                <Title variant="hero" as="h1">
                  Australia Post mail redirection in 2026: what it costs, what it covers, and the alternatives
                </Title>
                <Box className="d-flex justify-content-center">
                  <Text mr={3}>November 4, 2026</Text>
                  <Text>Michael Tippett</Text>
                </Box>
              </Col>
            </Row>
          </Container>
        </Section>
        <Section className="pb-0 pt-4">
          <Container>
            <Row>
              <Col lg="12" className="mb-5">
                <Post>
                  <div>
                    <img src={titleImage} alt="Letterbox at an old address, the mail Australia Post redirection diverts" className={styles.titleImage} />
                  </div>
                  <div>
                    <p>Australia Post mail redirection sends letters and parcels addressed to your old address on to a new one, for a fixed period you pay for upfront. It is the default choice when you move house, and for a domestic move it is usually the right one. This guide sets out the current prices, including the concession rate pensioners use, what redirection will not handle, and the situations where a mail service such as HotSnail fits better.</p>

                    <p>Australia Post updated its mail redirection and mail hold retail prices from 1 July 2026. The figures below were taken from auspost.com.au in November 2026. Prices change, so confirm them on the <a href="https://auspost.com.au/personal/receiving/mail-redirection-and-mail-hold/redirection" target="_blank" rel="noopener noreferrer">Australia Post mail redirection page</a> before you apply.</p>

                    <h2>How Australia Post mail redirection works</h2>
                    <p>Once your application is approved, Australia Post intercepts mail addressed to you at your old address and delivers it to the new address instead. The main rules:</p>
                    <ul>
                      <li><b>Periods.</b> You choose 1, 3, 6 or 12 months, and you can extend, alter or cancel the service later.</li>
                      <li><b>Names.</b> Up to six names can be included on one application, for example the people you live with, a maiden name or an alias. Mail addressed to anyone not listed is not redirected.</li>
                      <li><b>Letters and parcels.</b> Domestic redirection covers eligible letters and parcels carried by Australia Post.</li>
                      <li><b>Identity.</b> You need proof of identity, and a concession card for the concession rate.</li>
                      <li><b>Where to apply.</b> Personal and concession redirection within Australia can be applied for online. International redirection and home office redirection are applied for at a Post Office.</li>
                      <li><b>Optional notifications.</b> You can opt in to have Australia Post tell selected organisations, such as insurers and energy providers, about your new address.</li>
                    </ul>
                    <p>Apply before your moving date rather than after it. Australia Post's FAQ lists how long setup takes, and mail delivered to the old address before the service starts is not recovered.</p>

                    <h2>Mail redirection cost in 2026</h2>
                    <p>Pricing is the same in every state, so "mail redirection QLD" or "mail redirection NSW" costs the same as anywhere else. These are the personal rates:</p>
                    <div className="table-scroll">
                      <table>
                        <thead>
                          <tr>
                            <th>Personal redirection to</th>
                            <th>Up to 1 month</th>
                            <th>Up to 3 months</th>
                            <th>Up to 6 months</th>
                            <th>Up to 12 months</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>An Australian address</td>
                            <td>$40.50</td>
                            <td>$81</td>
                            <td>$121.50</td>
                            <td>$202.50</td>
                          </tr>
                          <tr>
                            <td>An overseas address</td>
                            <td>$81</td>
                            <td>$162</td>
                            <td>$243</td>
                            <td>$405</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <p>The 12-month domestic service works out at under $17 a month, which is good value for a straightforward move. The overseas rates are double the domestic ones.</p>

                    <h2>Concession and pensioner mail redirection</h2>
                    <p>If you hold a valid concession card, such as a Pensioner Concession Card, or you are listed as a dependant on someone else's card, Australia Post offers a reduced rate. If several people at the same address are on the application, each one needs a valid card to get the concession.</p>
                    <div className="table-scroll">
                      <table>
                        <thead>
                          <tr>
                            <th>Concession redirection to</th>
                            <th>Up to 1 month</th>
                            <th>Up to 3 months</th>
                            <th>Up to 6 months</th>
                            <th>Up to 12 months</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>An Australian address</td>
                            <td>$24.30</td>
                            <td>$48.60</td>
                            <td>$72.90</td>
                            <td>$121.50</td>
                          </tr>
                          <tr>
                            <td>An overseas address</td>
                            <td>$48.60</td>
                            <td>$97.20</td>
                            <td>$145.80</td>
                            <td>$243</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    <h3>Free mail redirection</h3>
                    <p>Australia Post may redirect mail free of charge for people affected by family and domestic violence or a natural disaster, and for people managing a deceased estate. Eligibility is set out on its special circumstances page. If you are handling an estate, our <Link to="/guides/deceased-estate-mail-management-australia/">deceased estate mail guide</Link> covers the rest of the process.</p>

                    <h3>Home office, SMSF and trust redirection</h3>
                    <p>If you run a business, trust or self-managed super fund from home, Australia Post sells a home office service, applied for in person at a Post Office. Within Australia it is $81 for up to 1 month, $202.50 for 3 months, $405 for 6 months and $810 for 12 months. Businesses at commercial premises use Australia Post's separate business redirection service.</p>

                    <h2>What mail redirection does not cover</h2>
                    <p>Most problems with redirection come from expecting it to catch everything. It will not:</p>
                    <ul>
                      <li><b>Redirect couriers.</b> It only covers items Australia Post carries. Parcels sent by other couriers still go to the old address.</li>
                      <li><b>Redirect names you left off.</b> Only the names on the application are redirected.</li>
                      <li><b>Send everything overseas.</b> Australia Post says Registered Post, Cash on Delivery, Parcel Post, Express Post and items that need customs documentation cannot be redirected overseas.</li>
                      <li><b>Bring mail back to Australia.</b> It cannot redirect mail from an overseas address to an Australian one. That is up to the postal service in the other country.</li>
                      <li><b>Last forever.</b> When the period ends, mail goes back to the old address. Redirection buys you time to update your address with banks, the ATO and everyone else. Our <Link to="/guides/address-change-checklist-australia/">address change checklist</Link> lists who to tell.</li>
                    </ul>

                    <h2>Mail hold: the alternative when you are away</h2>
                    <p>If you are travelling rather than moving, mail hold may suit you better. Australia Post stores your letters and parcels for between 1 week and 12 months, then delivers them to your address when the hold ends. Personal mail hold starts from $30.70 and concession mail hold from $18.40, and home office mail hold is $79 a month. The catch is that you cannot see or act on anything until you are home. We compare the two in detail in <Link to="/compare/mail-redirection-vs-mail-hold-australia/">mail redirection vs mail hold</Link>.</p>

                    <h2>When a mail service fits better than redirection</h2>
                    <p>Redirection is built for a move from one Australian address to another. It is a poorer fit when:</p>
                    <ul>
                      <li><b>You are leaving Australia for longer than a year.</b> Overseas redirection costs $405 for 12 months, excludes several mail types, and still ends. A mail service gives you an Australian address you keep for as long as you need it.</li>
                      <li><b>You need to read mail, not just receive it.</b> A redirected letter still has to travel to you. A mail service scans the envelope, opens and scans the contents if you ask, and you read it online the same way wherever you are.</li>
                      <li><b>You have no fixed address yet.</b> Redirection needs somewhere to send the mail. If you are between homes, travelling, or renting short term, a fixed mailing address avoids changing it again every few months.</li>
                      <li><b>You want to choose what gets forwarded.</b> With a mail service you forward only what you need, bundle items together, and shred the rest.</li>
                    </ul>
                    <p>HotSnail gives every customer an Australian PO Box on the Gold Coast, and a Gold Coast street address on request after signup at no extra cost. Mail is scanned, and you decide item by item whether to open and scan it, forward it within Australia or overseas, return it to the sender or shred it. You can point Australia Post redirection at your HotSnail address while you update your details with everyone else. The <Link to="/compare/auspost-mail-redirection-vs-virtual-mailbox/">side-by-side comparison of redirection and a virtual mailbox</Link> goes through the trade-offs, and <Link to="/pricing/">our pricing page</Link> lists the plans.</p>

                    <h2>Australia Post mail redirection: quick answers</h2>
                    <h3>How much does mail redirection cost?</h3>
                    <p>From $40.50 for one month within Australia, $81 for three months, $121.50 for six months and $202.50 for twelve months, at the personal rate in November 2026.</p>
                    <h3>Is there a pensioner discount?</h3>
                    <p>Yes. Concession card holders, including pensioners, pay the concession rate, from $24.30 for one month within Australia.</p>
                    <h3>Does mail redirection include parcels?</h3>
                    <p>Domestic redirection covers eligible letters and parcels carried by Australia Post. Parcels sent with other couriers are not redirected, and several parcel types cannot be redirected overseas.</p>
                    <h3>Can I redirect mail overseas?</h3>
                    <p>Yes, at double the domestic rate, by applying at a Post Office. Registered Post, Cash on Delivery, Parcel Post, Express Post and items needing customs documentation are excluded.</p>

                    <p>
                      If you are heading overseas or need an address that outlasts your redirection,{" "}
                      <a href="https://members.hotsnail.com.au/signup">set up a HotSnail mailbox</a>{" "}
                      and read your Australian mail from anywhere. If you are still deciding, our{" "}
                      <Link to="/guides/virtual-mailbox-australia-setup-guide/">virtual mailbox setup guide</Link>{" "}
                      explains how it works day to day.
                    </p>
                  </div>
                </Post>
                <Box className="d-flex" mt={4}>
                  <BadgePost>Mail redirection</BadgePost>
                  <BadgePost>Australia Post</BadgePost>
                  <BadgePost>Moving house</BadgePost>
                  <BadgePost>Mail forwarding</BadgePost>
                </Box>
              </Col>
            </Row>
          </Container>
        </Section>
      </PageWrapper>
    </>
  );
};

export default PageDetails;
